import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Toast from '../Common/Toast';
import Pagination from '../Common/Pagination';

const isAdminUser = (user) => {
  return !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
};

const formatImportStatus = (s) => {
  if (s === 'running') return 'Выполняется';
  if (s === 'pending') return 'В очереди';
  if (s === 'done') return 'Завершено';
  if (s === 'failed') return 'Ошибка';
  return s || '—';
};

const ClassifiersList = () => {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  const [importJob, setImportJob] = useState(null);
  const importJobRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importStarting, setImportStarting] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      while (true) {
        const query = `?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ''}`;
        const resp = await apiService.getClassifiers(query);
        const chunk = resp.data.results || [];
        all.push(...chunk);
        if (!resp.data.next) break;
        page += 1;
        if (page > 200) break;
      }
      setItems(all);
    } catch (err) {
      setError('Ошибка при загрузке классификаторов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [search]);

  const loadLatestImportJob = useCallback(async () => {
    if (!isAdmin) return;
    setImportLoading(true);
    try {
      const resp = await apiService.listClassifierImportJobs('?page=1');
      const first = (resp.data?.results || [])[0] || null;
      setImportJob(first);
    } catch (err) {
      console.error('Не удалось загрузить статус импорта классификаторов', err);
    } finally {
      setImportLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
        }
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (isAdmin) loadLatestImportJob();
  }, [isAdmin, loadLatestImportJob]);

  useEffect(() => {
    importJobRef.current = importJob;
  }, [importJob]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const timer = setInterval(async () => {
      const job = importJobRef.current;
      if (!job?.id) return;
      if (job.status !== 'pending' && job.status !== 'running') return;
      try {
        const resp = await apiService.getClassifierImportJob(job.id);
        setImportJob(resp.data);
      } catch (_) {}
    }, 1200);
    return () => clearInterval(timer);
  }, [isAdmin]);

  const startImport = async () => {
    if (!isAdmin) return;
    setImportStarting(true);
    try {
      const resp = await apiService.startClassifierImport();
      setImportJob(resp.data);
      showToast('Импорт классификаторов запущен');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      showToast('Не удалось запустить импорт классификаторов: ' + msg, 'error');
    } finally {
      setImportStarting(false);
    }
  };

  const refreshImportStatus = async () => {
    if (!isAdmin || !importJob?.id) return;
    try {
      const resp = await apiService.getClassifierImportJob(importJob.id);
      setImportJob(resp.data);
    } catch (_) {}
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, items.length]);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return items.slice(start, start + itemsPerPage);
  }, [items, currentPage, itemsPerPage]);

  const handleItemsPerPageChange = (v) => {
    setItemsPerPage(v);
    setCurrentPage(1);
    apiService.saveUiSettings({ table_page_size: v }).catch(() => {});
  };

  const progressPct = Math.max(
    0,
    Math.min(100, (importJob?.total_pages ? (importJob.current_page / importJob.total_pages) * 100 : 0)),
  );

  if (loading) {
    return <div style={{ padding: 50, textAlign: 'center' }}><h2>Загрузка классификаторов...</h2></div>;
  }

  return (
    <div style={{ width: '100%', maxWidth: 1300, margin: '0 auto', padding: 20 }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <h1>🗂️ Классификаторы</h1>

      {error && (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#fff3cd', border: '1px solid #ffe69c' }}>
          {error}
        </div>
      )}

      {isAdmin && (
        <div style={{ marginBottom: 14, padding: 12, border: '1px solid #dee2e6', borderRadius: 10, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
            <button
              type="button"
              onClick={startImport}
              disabled={importStarting || importJob?.status === 'running' || importJob?.status === 'pending'}
              style={{ padding: '8px 12px', border: '1px solid #0d6efd', background: '#0d6efd', color: '#fff', borderRadius: 8 }}
              title="Повторный импорт из файла Классификатор имущества.xlsx"
            >
              {importStarting ? 'Запуск…' : 'Импорт с fedresurs.ru'}
            </button>
            <button type="button" onClick={refreshImportStatus} disabled={!importJob?.id} style={{ padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: 8, background: '#fff' }}>
              Обновить статус
            </button>
          </div>
          <div style={{ fontSize: 14, color: '#495057', marginBottom: 6 }}>
            Статус импорта: <b>{formatImportStatus(importJob?.status)}</b>
            {importLoading && <span> · загрузка…</span>}
          </div>
          <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 8 }}>
            {importJob
              ? `стр. ${importJob.current_page || 0}/${importJob.total_pages || 0} · обработано ${importJob.processed || 0} · создано ${importJob.created || 0} · обновлено ${importJob.updated || 0} · пропущено ${importJob.skipped || 0}`
              : 'Задания импорта пока не запускались'}
          </div>
          <div style={{ width: '100%', height: 8, borderRadius: 999, background: '#e9ecef', overflow: 'hidden' }}>
            <div style={{ width: `${progressPct}%`, height: '100%', background: importJob?.status === 'failed' ? '#dc3545' : '#0d6efd', transition: 'width 0.2s ease' }} />
          </div>
          {!!importJob?.message && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#6c757d', whiteSpace: 'pre-wrap' }}>
              {String(importJob.message).trim()}
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <input
          type="text"
          placeholder="Поиск по коду и наименованию..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 240, padding: '10px 12px', borderRadius: 8, border: '1px solid #ced4da' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#6c757d', fontSize: 14 }}>На странице:</span>
          <select value={itemsPerPage} onChange={(e) => handleItemsPerPageChange(Number(e.target.value))} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ced4da' }}>
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
            <option value={96}>96</option>
          </select>
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #e9ecef', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e9ecef', width: 160 }}>Код</th>
              <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Наименование</th>
              <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e9ecef', width: 120 }}>Уровень</th>
              <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e9ecef', width: 180 }}>Родитель</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: 18, color: '#6c757d', fontSize: '13px' }}>Ничего не найдено</td>
              </tr>
            ) : (
              paginated.map((item) => (
                <tr key={item.id}>
                  <td style={{ padding: 10, borderBottom: '1px solid #f1f3f5', fontFamily: 'monospace', fontSize: '13px' }}>{item.code}</td>
                  <td style={{ padding: 10, borderBottom: '1px solid #f1f3f5', fontSize: '13px' }}>{item.name}</td>
                  <td style={{ padding: 10, borderBottom: '1px solid #f1f3f5', fontSize: '13px' }}>{item.level}</td>
                  <td style={{ padding: 10, borderBottom: '1px solid #f1f3f5', fontFamily: 'monospace', fontSize: '13px' }}>{item.parent_code || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default ClassifiersList;
